import React, { useEffect, useMemo, useState } from 'react';
import {
 FontIcon, Spinner, Stack, Text,
} from '@fluentui/react';
import { extractAllPdfsFromUblXml } from './extractPdfFromUblXml';
import BillitUblMarkupView from './BillitUblMarkupView';
import { BillitPdfEmbed, billitColors } from './billitDetailUi';

type Props = {
  title?: string;
  pdfUrl: string | null;
  pdfLoading?: boolean;
  pdfError?: string | null;
  ublXmlContent?: string | null;
  height?: number | string;
  fillContainer?: boolean;
  fileName?: string;
};

const BillitInvoiceDocumentView = ({
  title = '',
  pdfUrl,
  pdfLoading = false,
  pdfError = null,
  ublXmlContent = null,
  height = 460,
  fillContainer = false,
  fileName = 'factuur.pdf',
}: Props) => {
  const xmlFileName = fileName.replace(/\.pdf$/i, '.xml');

  // Extra documenten in de UBL (bv. leveringsbonnen van Van Herck) naast de factuur.
  const extraPdfs = useMemo(
    () => (ublXmlContent ? extractAllPdfsFromUblXml(ublXmlContent).slice(1) : []),
    [ublXmlContent],
  );
  const [selected, setSelected] = useState(0); // 0 = factuur
  const extraUrl = useMemo(
    () => (selected > 0 && extraPdfs[selected - 1] ? URL.createObjectURL(extraPdfs[selected - 1].blob) : null),
    [selected, extraPdfs],
  );
  useEffect(() => () => { if (extraUrl) URL.revokeObjectURL(extraUrl); }, [extraUrl]);
  useEffect(() => setSelected(0), [ublXmlContent]);

  if (pdfLoading) {
    return (
      <Stack horizontalAlign='center' style={{ padding: 24 }}>
        <Spinner label='Document laden...' />
      </Stack>
    );
  }

  if (pdfUrl && extraPdfs.length === 0) {
    // geen extra documenten: ongewijzigde weergave
    return (
      <BillitPdfEmbed
        title={title}
        url={pdfUrl}
        loading={false}
        error={pdfError}
        height={height}
        fillContainer={fillContainer}
        fileName={fileName}
      />
    );
  }

  if (pdfUrl) {
    const tabs = (
      <div
        style={{
 display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8,
}}
        role='tablist'
        aria-label='Documenten'
      >
        {[{ label: 'Factuur', icon: 'Page' }, ...extraPdfs.map(pdf => ({ label: `Bijlage: ${pdf.fileName}`, icon: 'Attach' }))]
          .map((tab, index) => {
            const active = index === selected;
            return (
              <button
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                type='button'
                role='tab'
                aria-selected={active}
                onClick={() => setSelected(index)}
                title={tab.label}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  maxWidth: 260,
                  padding: '4px 10px',
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: `1px solid ${active ? billitColors.text : '#c8c6c4'}`,
                  background: active ? billitColors.text : '#fff',
                  color: active ? '#fff' : billitColors.text,
                }}
              >
                <FontIcon iconName={tab.icon} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tab.label}</span>
              </button>
            );
          })}
      </div>
    );
    const showExtra = selected > 0 && extraUrl;
    return (
      <div
        style={fillContainer ? {
          display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%',
        } : undefined}
      >
        {tabs}
        <div
          style={fillContainer ? {
            display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
          } : undefined}
        >
          <BillitPdfEmbed
            key={showExtra ? `extra-${selected}` : 'factuur'}
            title={showExtra ? extraPdfs[selected - 1].fileName : title}
            url={showExtra ? extraUrl : pdfUrl}
            loading={false}
            error={showExtra ? null : pdfError}
            height={height}
            fillContainer={fillContainer}
            fileName={showExtra ? extraPdfs[selected - 1].fileName : fileName}
          />
        </div>
      </div>
    );
  }

  if (ublXmlContent) {
    return (
      <BillitUblMarkupView
        title={title || 'UBL factuur'}
        xmlContent={ublXmlContent}
        height={height}
        fillContainer={fillContainer}
        fileName={xmlFileName}
      />
    );
  }

  return (
    <Text style={{ color: billitColors.textMuted, fontSize: 12 }}>
      Geen PDF of UBL XML beschikbaar voor deze factuur.
    </Text>
  );
};

export default BillitInvoiceDocumentView;
